<template>
  <div>
    <v-row>
      <v-col cols="12" v-for="(item, i) in savedPlans" :key="i">
        <Step2Panels
          :item="item"
          :index="i"
          @setDeletePlanOffset="setDeletePlanOffset"
        />
      </v-col>
    </v-row>
    <v-divider class="my-6"></v-divider>
    <div v-if="lastSavedPlan && lastSavedPlan.hasNoLimits">
      <p
        class="subtitle-1 font-weight-medium text-center"
        style="color:#2661ff"
      >
        {{ $t('pricing_model.edit.add_currency.plan_without_limit.title') }}
      </p>
      <p class="subtitle-2 text-center">
        {{
          $t('pricing_model.edit.add_currency.plan_without_limit.description')
        }}
      </p>
    </div>
    <div v-else>
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-row no-gutters>
          <!------------------------------------------>
          <!-- NAME ---------------------------------->
          <!------------------------------------------>
          <v-col cols="12" md="5">
            <span class="subtitle-2">{{ inputs.planName.title }}</span>
            <v-text-field
              v-model="plan.name"
              :placeholder="inputs.planName.placeholder"
              class="rounded-lg mt-1"
              outlined
              dense
              :rules="inputs.planName.rules"
              required
            >
              <v-tooltip slot="append" bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="grey lighten-1" size="22">
                    help
                  </v-icon>
                </template>
                <span style="max-width: 200px;">{{
                  inputs.planName.tooltip
                }}</span>
              </v-tooltip>
            </v-text-field>
          </v-col>
          <!------------------------------------------>
          <!-- FREQUENCY ----------------------------->
          <!------------------------------------------>
          <v-col cols="6" md="3" class="text-center ml-4">
            <span class="subtitle-2">{{ inputs.planFrequency.title }}</span>
            <v-select
              v-if="localPricingModel.frequency === 3"
              v-model="plan.frequency"
              :items="getFrequencyOptions"
              :placeholder="inputs.planFrequency.title"
              item-text="title"
              item-value="value"
              class="rounded-lg mt-1"
              :rules="inputs.planFrequency.rules"
              outlined
              dense
            />
            <div v-else>
              <p class="subtitle-2 font-weight-regular mt-3">
                {{ getFrequencyName(plan.frequency) }}
              </p>
            </div>
          </v-col>
          <!------------------------------------------>
          <!-- COLOR --------------------------------->
          <!------------------------------------------>
          <v-col cols="6" md="3" class="color-picker-column">
            <span class="subtitle-2 text-center">{{
              inputs.planColor.title
            }}</span>
            <div
              class="d-flex justify-center align-center mt-2"
              style="gap: 8px;"
            >
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <v-icon v-if="!plan.color" color="grey">palette</v-icon>
                    <div
                      v-if="plan.color"
                      class="color-swatch"
                      :style="{ backgroundColor: plan.color.hex }"
                    ></div>
                  </div>
                </template>

                <v-color-picker
                  v-model="plan.color"
                  dot-size="25"
                  hide-canvas
                  hide-inputs
                  hide-mode-switch
                  hide-sliders
                  show-swatches
                  swatches-max-height="200"
                ></v-color-picker>
              </v-menu>
            </div>
          </v-col>
          <!------------------------------------------>
          <!-- DESCRIPTION --------------------------->
          <!------------------------------------------>
          <v-col cols="12" md="12">
            <span class="subtitle-2">{{ inputs.planDescription.title }}</span>
            <v-text-field
              v-model="plan.description"
              :placeholder="inputs.planDescription.placeholder"
              class="rounded-lg mt-1"
              outlined
              dense
              :rules="inputs.planDescription.rules"
              required
            >
              <v-tooltip slot="append" bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="grey lighten-1" size="22"
                    >help</v-icon
                  >
                </template>
                <span style="max-width: 200px;">{{
                  inputs.planDescription.tooltip
                }}</span>
              </v-tooltip>
            </v-text-field>
          </v-col>
          <v-col cols="12" class="d-flex my-4">
            <p class="primary-color subtitle-2">Configura tu plan</p>
            <v-divider
              class="mt-3"
              inset
              style="border-color: #2661ff;"
            ></v-divider>
          </v-col>
          <!------------------------------------------>
          <!-- SWITCH NO LIMIT ----------------------->
          <!------------------------------------------>
          <v-col cols="12" v-if="localPricingModel.hasLimitsTable">
            <div class="d-flex align-center justify-end" style="width: 100%;">
              <v-switch
                :disabled="lastHasNoLimit"
                label="Último límite"
                color="#2661FF"
                inset
                class="ml-6"
                @change="toggleNoLimit(plan)"
              ></v-switch>
            </div>
          </v-col>

          <!------------------------------------------>
          <!-- PLANS BY CURRENCY --------------------->
          <!------------------------------------------>
          <v-col cols="12">
            <v-row
              v-for="(currencyPlan,
              currencyByPlanIndex) in plan.plansByCurrency"
              :key="currencyByPlanIndex"
            >
              <!------------------------------------------>
              <!-- CURRENCIES ---------------------------->
              <!------------------------------------------>
              <v-col cols="12" md="2">
                <p class="subtitle-2">
                  {{ inputs.planCurrency.title }}
                  <v-tooltip slot="append" bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" color="grey lighten-1" size="22">
                        help
                      </v-icon>
                    </template>
                    <span style="max-width: 200px;">
                      {{ inputs.planDescription.tooltip }}
                    </span>
                  </v-tooltip>
                </p>
                <span class="text-center">
                  {{ getCurrencyCode(currencyPlan.currency_id).code }}
                </span>
              </v-col>

              <v-col cols="12">
                <v-card
                  class="custom_card pa-4"
                  flat
                  style="border: 1px solid #BCBCBC"
                >
                  <v-card-text>
                    <v-row>
                      <!------------------------------------------>
                      <!-- AMOUNT -------------------------------->
                      <!------------------------------------------>
                      <v-col cols="12" md="6">
                        <div class="d-flex align-center">
                          <span class="subtitle-2">
                            {{ inputs.planAmount.title }}
                            <v-tooltip slot="append" bottom>
                              <template v-slot:activator="{ on }">
                                <v-icon
                                  v-on="on"
                                  color="grey lighten-1"
                                  size="18"
                                >
                                  help
                                </v-icon>
                              </template>
                              <span style="max-width: 200px;">
                                {{ inputs.planAmount.tooltip }}
                              </span>
                            </v-tooltip>
                          </span>
                          <v-switch
                            v-model="currencyPlan.hasCustomAmount"
                            :disabled="
                              localPricingModel.hasLimitsTable ||
                                pricingModelVersion2
                            "
                            :label="inputs.planAmount.switch"
                            color="#2661FF"
                            inset
                            class="ml-6"
                            @change="
                              customAmount(plan, currencyPlan.currency_id)
                            "
                          ></v-switch>
                        </div>
                        <div
                          v-if="!currencyPlan.hasCustomAmount"
                          class="d-flex justify-center align-center"
                        >
                          <span style="font-size: 12px;" class="mb-4 mr-4">
                            {{
                              `${
                                getCurrencyCode(currencyPlan.currency_id).code
                              } (${
                                getCurrencyCode(currencyPlan.currency_id).symbol
                              })`
                            }}
                          </span>
                          <v-text-field
                            v-if="!currencyPlan.hasCustomAmount"
                            v-model="currencyPlan.amount"
                            :placeholder="inputs.planAmount.placeholder"
                            class="rounded-lg mt-1"
                            outlined
                            dense
                            :disabled="currencyPlan.hasCustomAmount"
                            :rules="
                              !currencyPlan.hasCustomAmount
                                ? inputs.planAmount.rules
                                : ''
                            "
                            required
                          ></v-text-field>
                        </div>
                      </v-col>
                      <!------------------------------------------>
                      <!-- FEE_PERCENTAGE ------------------------>
                      <!------------------------------------------>
                      <v-col cols="12" md="6">
                        <div class="d-flex align-center">
                          <span class="subtitle-2"
                            >{{ inputs.planFee.title }}
                            <v-tooltip slot="append" bottom>
                              <template v-slot:activator="{ on }">
                                <v-icon
                                  v-on="on"
                                  color="grey lighten-1"
                                  size="18"
                                >
                                  help
                                </v-icon>
                              </template>
                              <span style="max-width: 200px;">
                                {{ inputs.planFee.tooltip }}
                              </span>
                            </v-tooltip>
                          </span>
                          <v-switch
                            v-model="currencyPlan.hasCustomFee"
                            :disabled="
                              localPricingModel.hasLimitsTable ||
                                pricingModelVersion2
                            "
                            :label="inputs.planFee.switch"
                            color="#2661FF"
                            inset
                            class="ml-6"
                            @change="customFee(plan, currencyPlan.currency_id)"
                          ></v-switch>
                        </div>
                        <div
                          v-if="!currencyPlan.hasCustomFee"
                          class="d-flex justify-center align-center"
                        >
                          <span class="mb-4 mr-4">
                            %
                          </span>
                          <v-text-field
                            v-if="!currencyPlan.hasCustomFee"
                            v-model="currencyPlan.fee_percentage"
                            :placeholder="inputs.planFee.placeholder"
                            class="rounded-lg mt-1"
                            outlined
                            dense
                            :rules="inputs.planFee.rules"
                            required
                          ></v-text-field>
                        </div>
                      </v-col>

                      <!------------------------------------------>
                      <!-- LIMITS TABLE -------------------------->
                      <!------------------------------------------>
                      <v-col cols="12">
                        <v-row v-if="localPricingModel.hasLimitsTable">
                          <!------------------------------------------>
                          <!-- LIMIT REGISTERS ----------------------->
                          <!------------------------------------------>
                          <v-col cols="12" v-if="!pricingModelVersion2">
                            <div class="d-flex align-center">
                              <span class="subtitle-2">
                                {{ inputs.planLimitRegisters.title }}
                                <v-tooltip slot="append" bottom>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      v-on="on"
                                      color="grey lighten-1"
                                      size="18"
                                    >
                                      help
                                    </v-icon>
                                  </template>
                                  <span style="max-width: 200px;">
                                    {{ inputs.planLimitRegisters.tooltip }}
                                  </span>
                                </v-tooltip></span
                              >
                            </div>
                            <v-row>
                              <v-col cols="12" md="6" class="mt-4 d-flex">
                                <span class="mr-4 mt-3">
                                  {{ inputs.planLimitRegisters.from }}
                                </span>
                                <v-text-field
                                  v-model="currencyPlan.registers_offset"
                                  placeholder="Sin límite"
                                  disabled
                                  class="rounded-lg mt-1"
                                  outlined
                                  dense
                                  :rules="inputs.planLimitRegisters.rules"
                                  required
                                ></v-text-field>
                              </v-col>
                              <v-col
                                cols="12"
                                md="6"
                                class="mt-4 d-flex"
                                v-if="validateNoLimits(plan.hasNoLimits)"
                              >
                                <span class="mr-4 mt-3">{{
                                  inputs.planLimitRegisters.to
                                }}</span>
                                <v-text-field
                                  v-model="currencyPlan.registers_limit"
                                  :placeholder="
                                    inputs.planLimitRegisters.placeholder
                                  "
                                  class="rounded-lg mt-1"
                                  outlined
                                  type="number"
                                  dense
                                  :disabled="
                                    !validateNoLimits(plan.hasNoLimits)
                                  "
                                  :rules="[
                                    validateLimit(
                                      currencyPlan.registers_limit,
                                      'registers_offset',
                                      currencyByPlanIndex
                                    )
                                  ]"
                                  required
                                ></v-text-field>
                              </v-col>
                            </v-row>
                          </v-col>
                          <!------------------------------------------>
                          <!-- LIMIT INCOMES ------------------------->
                          <!------------------------------------------>
                          <v-col cols="12">
                            <div class="d-flex align-center">
                              <span class="subtitle-2"
                                >{{ inputs.planLimitIncomes.title }}
                                <v-tooltip slot="append" bottom>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      v-on="on"
                                      color="grey lighten-1"
                                      size="18"
                                      >help</v-icon
                                    >
                                  </template>
                                  <span style="max-width: 200px;">
                                    {{ inputs.planLimitIncomes.tooltip }}
                                  </span>
                                </v-tooltip></span
                              >
                            </div>
                            <v-row>
                              <v-col cols="12" md="6" class="mt-4 d-flex">
                                <span class="mr-4 mt-3">{{
                                  inputs.planLimitIncomes.from
                                }}</span>
                                <v-text-field
                                  v-model="currencyPlan.incomes_offset"
                                  placeholder="Sin límite"
                                  disabled
                                  class="rounded-lg mt-1"
                                  :label="
                                    `${
                                      getCurrencyCode(currencyPlan.currency_id)
                                        .code
                                    } (${
                                      getCurrencyCode(currencyPlan.currency_id)
                                        .symbol
                                    })`
                                  "
                                  outlined
                                  dense
                                  :rules="inputs.planLimitIncomes.rules"
                                  required
                                ></v-text-field>
                              </v-col>
                              <v-col
                                cols="12"
                                md="6"
                                class="mt-4 d-flex"
                                v-if="validateNoLimits(plan.hasNoLimits)"
                              >
                                <span class="mr-4 mt-3">{{
                                  inputs.planLimitIncomes.to
                                }}</span>
                                <v-text-field
                                  v-model="currencyPlan.incomes_limit"
                                  :placeholder="
                                    inputs.planLimitIncomes.placeholder
                                  "
                                  :label="
                                    `${
                                      getCurrencyCode(currencyPlan.currency_id)
                                        .code
                                    } (${
                                      getCurrencyCode(currencyPlan.currency_id)
                                        .symbol
                                    })`
                                  "
                                  class="rounded-lg mt-1"
                                  outlined
                                  type="number"
                                  dense
                                  :disabled="
                                    !validateNoLimits(plan.hasNoLimits)
                                  "
                                  required
                                  :rules="[
                                    validateLimit(
                                      currencyPlan.incomes_limit,
                                      'incomes_offset',
                                      currencyByPlanIndex
                                    )
                                  ]"
                                ></v-text-field>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-divider></v-divider>
                  <!------------------------------------------>
                  <!-- ADDITIONAL SERVICES ------------------->
                  <!------------------------------------------>
                  <v-card-text class="mb-4">
                    <v-row>
                      <v-col cols="12">
                        <p class="subtitle-2">
                          {{ inputs.planAdditionalServices.title }}
                          <v-tooltip slot="append" bottom>
                            <template v-slot:activator="{ on }">
                              <v-icon
                                v-on="on"
                                color="grey lighten-1"
                                size="18"
                              >
                                help
                              </v-icon>
                            </template>
                            <span style="max-width: 200px;">
                              {{ inputs.planAdditionalServices.tooltip }}
                            </span>
                          </v-tooltip>
                        </p>

                        <v-menu
                          ref="menu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on }">
                            <v-btn
                              class="rounded-xl"
                              small
                              outlined
                              color="#2661FF"
                              v-on="on"
                            >
                              <v-icon>add</v-icon>
                              Agregar
                            </v-btn>
                          </template>
                          <v-card class="rounded-lg" flat width="250">
                            <v-list dense>
                              <v-list-item-group
                                v-model="currencyPlan.selected_services"
                                multiple
                                color="#2661FF"
                              >
                                <v-list-item
                                  v-for="(item, i) in additionalServices"
                                  :key="i"
                                  @click="
                                    toggleService(item, plan, currencyPlan)
                                  "
                                >
                                  <template v-slot:default="{ active }">
                                    <v-list-item-action>
                                      <v-checkbox
                                        :input-value="active"
                                        color="#2661FF"
                                      ></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                      <v-list-item-title class="subtitle-2">
                                        {{
                                          $t(
                                            `pricing_model.create.step_2.services.types.${item.name}`
                                          )
                                        }}
                                      </v-list-item-title>
                                    </v-list-item-content>
                                  </template>
                                </v-list-item>
                              </v-list-item-group>
                            </v-list>
                          </v-card>
                        </v-menu>
                        <v-row>
                          <v-col
                            cols="12"
                            md="4"
                            class="mt-4"
                            v-for="(service,
                            service_index) in currencyPlan.additional_services"
                            :key="service_index"
                          >
                            <div>
                              <span class="subtitle-2">
                                {{
                                  $t(
                                    `pricing_model.create.step_2.services.types.${service.name}`
                                  )
                                }}
                              </span>
                              <!-- DISABLE CUSTOM SERVICE AMOUNT FOR NOW (TODO: INTEGREATE CUSTOM SERVICE AMOUNT) -->
                              <!-- <v-switch
                                v-model="service.hasCustomService"
                                :label="getServiceLabel(service.name)"
                                color="#2661FF"
                                inset
                                class="ml-6"
                                :disabled="pricingModelVersion2"
                                @change="
                                  customService(
                                    plan,
                                    currencyPlan.currency_id,
                                    service_index
                                  )
                                "
                              ></v-switch> -->
                            </div>
                            <v-text-field
                              v-if="
                                currencyPlan.additional_services &&
                                  !service.hasCustomService
                              "
                              v-model="service.amount_per_action"
                              :placeholder="
                                inputs.planAdditionalServices.placeholder
                              "
                              class="rounded-lg"
                              outlined
                              dense
                              :rules="inputs.planAdditionalServices.rules"
                              required
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
          <v-btn @click="savePlan" color="#2661FF" class="rounded-lg my-8">
            <span style="color: white;">{{ $t('actions.save') }}</span>
          </v-btn>
        </v-row>
      </v-form>
    </div>
    <v-card>
      <v-card-actions class="justify-end">
        <v-btn
          @click="$emit('backStep')"
          color="#2661FF"
          class="rounded-lg my-8"
          ><v-icon color="white">navigate_before</v-icon
          ><span style="color: white;">{{ $t('actions.go_back') }}</span></v-btn
        >
        <v-btn
          :disabled="savedPlans.length === 0 || lastSavedPlanWithoutLimits()"
          @click="createPricing"
          color="#2661FF"
          class="rounded-lg my-8"
          :loading="isCreatePricingLoading"
          ><span style="color: white;">{{ $t('actions.create') }}</span>
          <v-icon color="white">navigate_next</v-icon></v-btn
        >
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import Step2Panels from '@/components/PricingModels/Create/Step2Panels'

export default {
  name: 'PricingModelPlans',
  components: { Step2Panels },
  data() {
    return {
      step: 2,
      isCreatePricingLoading: false,
      valid: true,
      frequencies: [
        {
          title: this.$t('pricing_model.create.step_1.frequency.monthly'),
          value: 1
        },
        {
          title: this.$t('pricing_model.create.step_1.frequency.yearly'),
          value: 2
        }
      ],
      inputs: {
        planName: {
          title: this.$t('pricing_model.create.step_2.name.title'),
          placeholder: this.$t('pricing_model.create.step_2.name.title'),
          tooltip: this.$t('pricing_model.create.step_2.name.tooltip'),
          rules: [
            v => !!v || this.$t('pricing_model.create.step_2.name.rule'),
            v => {
              const existingPlan = this.savedPlans.find(plan => plan.name === v)
              return (
                !existingPlan ||
                this.$t('pricing_model.create.step_2.name.error')
              )
            }
          ]
        },
        planFrequency: {
          title: this.$t('pricing_model.create.step_2.frequency.title'),
          rules: [
            v => !!v || this.$t('pricing_model.create.step_2.frequency.rule')
          ]
        },
        planColor: {
          title: this.$t('pricing_model.create.step_2.color.title')
        },
        planDescription: {
          title: this.$t('pricing_model.create.step_2.description.title'),
          placeholder: this.$t('pricing_model.create.step_2.description.title'),
          tooltip: this.$t('pricing_model.create.step_2.description.tooltip'),
          rules: [
            v =>
              (!!v && v.length > 10) ||
              this.$t('pricing_model.create.step_2.description.rule')
          ]
        },
        planCurrency: {
          title: this.$t('pricing_model.create.step_2.currency.title')
        },
        planAmount: {
          title: this.$t('pricing_model.create.step_2.amount.title'),
          placeholder: this.$t(
            'pricing_model.create.step_2.amount.placeholder'
          ),
          switch: this.$t('pricing_model.create.step_2.amount.switch'),
          tooltip: this.$t('pricing_model.create.step_2.amount.tooltip'),
          rules: [
            v =>
              /^\d+(\.\d+)?$/.test(v) ||
              v === null ||
              this.$t('pricing_model.create.step_2.amount.rules.number'),
            v =>
              (v && v.toString().split('.').length < 2) ||
              (v && v.toString().split('.')[1].length <= 2) ||
              this.$t('pricing_model.create.step_2.amount.rules.decimal'),
            v =>
              (v && v >= 0) ||
              this.$t('pricing_model.create.step_2.amount.rules.min')
          ]
        },
        planFee: {
          title: this.$t('pricing_model.create.step_2.fee_percentage.title'),
          placeholder: this.$t(
            'pricing_model.create.step_2.fee_percentage.placeholder'
          ),
          switch: this.$t('pricing_model.create.step_2.fee_percentage.switch'),
          tooltip: this.$t(
            'pricing_model.create.step_2.fee_percentage.tooltip'
          ),
          rules: [
            v =>
              !!v ||
              this.$t(
                'pricing_model.create.step_2.fee_percentage.rules.required'
              ),
            v =>
              /^\d+(\.\d+)?$/.test(v) ||
              this.$t(
                'pricing_model.create.step_2.fee_percentage.rules.number'
              ),
            v =>
              (v && v.toString().split('.').length < 2) ||
              (v && v.toString().split('.')[1].length <= 2) ||
              this.$t(
                'pricing_model.create.step_2.fee_percentage.rules.decimal'
              ),
            v =>
              (v && v >= 0) ||
              this.$t('pricing_model.create.step_2.fee_percentage.rules.min'),
            v =>
              (v && v <= 100) ||
              this.$t('pricing_model.create.step_2.fee_percentage.rules.max')
          ]
        },
        planLimitRegisters: {
          title: this.$t('pricing_model.create.step_2.registers.title'),
          from: this.$t('pricing_model.create.step_2.registers.from'),
          to: this.$t('pricing_model.create.step_2.registers.to'),
          tooltip: this.$t('pricing_model.create.step_2.registers.tooltip'),
          placeholder: this.$t(
            'pricing_model.create.step_2.registers.placeholder'
          ),
          rules: [
            v =>
              !!v ||
              /^\d+$/.test(v) ||
              this.$t('pricing_model.create.step_2.registers.rule')
          ]
        },
        planLimitIncomes: {
          title: this.$t('pricing_model.create.step_2.incomes.title'),
          from: this.$t('pricing_model.create.step_2.incomes.from'),
          to: this.$t('pricing_model.create.step_2.incomes.to'),
          tooltip: this.$t('pricing_model.create.step_2.incomes.tooltip'),
          placeholder: this.$t(
            'pricing_model.create.step_2.incomes.placeholder'
          ),
          rules: [
            v =>
              !!v ||
              /^\d+$/.test(v) ||
              this.$t('pricing_model.create.step_2.incomes.rule')
          ]
        },
        planAdditionalServices: {
          title: this.$t('pricing_model.create.step_2.services.title'),
          tooltip: this.$t('pricing_model.create.step_2.services.tooltip'),
          placeholder: this.$t(
            'pricing_model.create.step_2.services.placeholder'
          ),
          switch: this.$t('pricing_model.create.step_2.services.switch'),
          rules: [
            v =>
              /^\d+(\.\d+)?$/.test(v) ||
              v === null ||
              this.$t('pricing_model.create.step_2.services.rules.number'),
            v =>
              (v && v.toString().split('.').length < 2) ||
              (v && v.toString().split('.')[1].length <= 6) ||
              this.$t('pricing_model.create.step_2.services.rules.decimal'),
            v =>
              (v && v >= 0) ||
              this.$t('pricing_model.create.step_2.services.rules.min')
          ]
        }
      },
      plan: {
        name: null,
        frequency: null,
        color: null,
        description: null,
        hasNoLimits: false,
        plansByCurrency: [] // Initialize as an empty array
      },
      localPricingModel: {},
      allowedCurrencies: []
    }
  },
  async created() {
    const { pricingModel, plan } = this
    this.localPricingModel = { ...this.pricingModel }
    this.setFrequencies()
    this.setAllowedCurrencies()
    await this.setPlansByCurrency()
    await this.getAdditionalServices()

    // Validate if pricingModel is not empty and if hasLimitsTable
    const setFirstOffsets =
      pricingModel &&
      Object.keys(pricingModel).length > 0 &&
      pricingModel.hasLimitsTable &&
      this.savedPlans.length === 0

    const setExistingOffsets =
      pricingModel &&
      Object.keys(pricingModel).length > 0 &&
      pricingModel.hasLimitsTable &&
      this.savedPlans.length > 0

    if (setFirstOffsets) {
      const { plansByCurrency } = plan
      plansByCurrency.map(currencyPlan => {
        currencyPlan.registers_offset = 0
        currencyPlan.incomes_offset = 0
      })
    } else if (setExistingOffsets) {
      const lastPlan = this.savedPlans[this.savedPlans.length - 1]
      const { plansByCurrency } = lastPlan
      plansByCurrency.map((currencyPlan, index) => {
        plan.plansByCurrency[index].incomes_offset = currencyPlan.incomes_limit
        plan.plansByCurrency[index].registers_offset =
          currencyPlan.registers_limit
      })
    }
  },
  computed: {
    ...mapState({
      pricingModel: ({ pricingModels: { pricingModel } }) => pricingModel,
      savedPlans: ({ pricingModels: { savedPlans } }) => savedPlans,
      additionalServices: ({ pricingModels: { additionalServices } }) =>
        additionalServices,
      currencies: ({ countries: { currencies } }) => currencies
    }),
    pricingModelVersion2() {
      return this.pricingModel.version === 2
    },
    getFrequencyOptions() {
      return this.frequencies
    },
    lastHasNoLimit() {
      // Disable limits switch if the last plan has no limit
      let lastPlan = this.savedPlans[this.savedPlans.length - 1]
      if (lastPlan && lastPlan.hasNoLimits) {
        return true
      } else {
        return false
      }
    },
    lastSavedPlan() {
      return this.savedPlans[this.savedPlans.length - 1]
    }
  },
  methods: {
    ...mapActions({
      getAdditionalServices: 'pricingModels/getAdditionalServices',
      createPricingModel: 'pricingModels/createPricingModel'
    }),
    ...mapMutations({
      setSavedPlans: 'pricingModels/setSavedPlans'
    }),
    getFrequencyName(frequency) {
      const matchedFrequency = this.frequencies.find(
        freq => freq.value === frequency
      )
      return matchedFrequency
        ? matchedFrequency.title
        : this.$t('pricing_model.create.step_2.frequency.unknown_frequency')
    },
    setFrequencies() {
      const [MONTHLY, YEARLY] = [1, 2]
      if ([MONTHLY, YEARLY].includes(this.pricingModel.frequency)) {
        this.plan.frequency = this.localPricingModel.frequency
      }
    },
    setAllowedCurrencies() {
      this.allowedCurrencies = this.currencies.filter(currency => {
        return this.pricingModel.currencies.includes(currency.id)
      })
    },
    async setPlansByCurrency() {
      for (const currency of this.allowedCurrencies) {
        this.plan.plansByCurrency.push({
          currency_id: currency.id,
          amount: null,
          fee_percentage: null,
          registers_offset: null,
          registers_limit: null,
          incomes_offset: null,
          incomes_limit: null,
          hasCustomAmount: false,
          hasCustomFee: false,
          additional_services: [],
          selected_services: []
        })
      }
    },
    getCurrencyCode(currency_id) {
      const currency = this.currencies.find(
        currency => currency.id === currency_id
      )
      return currency
    },
    customAmount(plan, currency_id) {
      const planByCurrency = this.getPlanByCurrency(plan, currency_id)
      if (planByCurrency.hasCustomAmount) {
        planByCurrency.amount = null
      }
    },
    customFee(plan, currency_id) {
      const planByCurrency = this.getPlanByCurrency(plan, currency_id)
      if (planByCurrency.hasCustomFee) {
        planByCurrency.fee_percentage = null
      }
    },
    toggleNoLimit(plan) {
      this.$set(plan, 'hasNoLimits', !plan.hasNoLimits)
      if (plan.hasNoLimits) {
        const { plansByCurrency } = plan
        plansByCurrency.forEach(currencyPlan => {
          this.$set(currencyPlan, 'registers_limit', null)
          this.$set(currencyPlan, 'incomes_limit', null)
        })
      }
    },
    getOffset(plan, property) {
      let lastPlan = this.savedPlans[this.savedPlans.length - 1]

      const propertyOffset =
        property === 'registers' ? 'registers_offset' : 'incomes_offset'
      const propertyLimit =
        property === 'registers' ? 'registers_limit' : 'incomes_limit'

      if (this.savedPlans.length > 0) {
        lastPlan.plansByCurrency.forEach(lastPlanByCurrency => {
          plan.plansByCurrency.forEach(currentPlanByCurrency => {
            if (
              lastPlanByCurrency.currency_id ===
              currentPlanByCurrency.currency_id
            ) {
              currentPlanByCurrency[propertyOffset] =
                lastPlanByCurrency[propertyLimit]
            }
          })
        })
      }
    },
    // Set the offsets of the deleted plan
    setDeletePlanOffset(deletedPlan, property) {
      const propertyName =
        property === 'registers' ? 'registers_offset' : 'incomes_offset'

      deletedPlan.plansByCurrency.forEach(deletedPlansByCurrency => {
        this.plan.plansByCurrency.forEach(currentPlanByCurrency => {
          if (
            deletedPlansByCurrency.currency_id ===
            currentPlanByCurrency.currency_id
          ) {
            currentPlanByCurrency[propertyName] =
              deletedPlansByCurrency[propertyName]
          }
        })
      })
    },
    validateLimit(value, offset, currencyByPlanIndex) {
      if (value === null) {
        return true // Allow null
      }

      const limit = parseInt(value, 10)
      const offsetValue = parseInt(
        this.plan.plansByCurrency[currencyByPlanIndex][offset],
        10
      )

      return (
        limit > offsetValue ||
        `${this.$t('pricing_model.edit.add_currency.limit_greater')} ${offset}`
      )
    },
    validateNoLimits(value) {
      let lastPlan = this.savedPlans[this.savedPlans.length - 1]
      // Find if the last plan has no limits so the table is finished
      if (value || (lastPlan && lastPlan.hasNoLimits)) {
        return false
      }
      return true
    },
    // AMOUNT - FEE PERCENTAGE - ADDITIONAL SERVICES
    getPlanByCurrency(plan, currency_id) {
      return plan.plansByCurrency.find(item => item.currency_id === currency_id)
    },
    toggleService(item, plan, currencyPlan) {
      const planByCurrency = this.getPlanByCurrency(
        plan,
        currencyPlan.currency_id
      )
      const index = planByCurrency.additional_services.findIndex(
        selectedItem => selectedItem.name === item.name
      )
      if (index !== -1) {
        planByCurrency.additional_services.splice(index, 1) // Item exists, remove it
      } else {
        // Service doesn't exist, add it
        const newService = {
          ...item, // Copy properties from item
          active: true, // Set initial active state as true or false as needed
          amount_per_action: null // Modify as needed
        }
        this.$set(
          planByCurrency.additional_services,
          planByCurrency.additional_services.length,
          newService
        )
      }
    },
    customService(plan, currency_id, service_index) {
      const planByCurrency = this.getPlanByCurrency(plan, currency_id)
      const service = planByCurrency.additional_services[service_index]

      if (service.hasCustomService) {
        service.amount_per_action = null
      }
    },
    getServiceLabel(name) {
      return `${this.$t(
        `pricing_model.create.step_2.services.types.${name}`
      )} ${this.inputs.planAdditionalServices.switch}`
    },
    lastSavedPlanWithoutLimits() {
      const lastSavedPlan = this.savedPlans[this.savedPlans.length - 1]
      return this.pricingModel.hasLimitsTable && !lastSavedPlan.hasNoLimits
    },
    async resetForm() {
      await this.$refs.form.reset()
    },
    async savePlan() {
      try {
        if (await this.$refs.form.validate()) {
          // Create a copy of this.plan
          const planCopy = JSON.parse(JSON.stringify(this.plan)) // Create a copy of this.plan

          // Reset the form and set the plan to a new empty object
          await this.resetForm()
          this.plan = {
            name: null,
            frequency: planCopy.frequency,
            color: null,
            description: null,
            hasNoLimits: false,
            plansByCurrency: [] // Set plansByCurrency to a new empty array
          }

          // Call functions before setting this.plan to defaultPlan
          this.setSavedPlans(planCopy) // Push the copy to savedPlans
          await this.setPlansByCurrency() // Fills plansByCurrency
          this.getOffset(this.plan, 'registers') // Update the registers_offset
          this.getOffset(this.plan, 'incomes') // Update the incomes_offset

          this.$noty.success(
            this.$t('pricing_model.messages.saved_plan_success')
          )
        } else {
          this.$noty.error(this.$t('pricing_model.messages.verifiy_data'))
        }
      } catch (error) {
        this.$noty.error(this.$t('pricing_model.messages.saved_plan_error'))
      }
    },
    async createPricing() {
      try {
        this.isCreatePricingLoading = true

        await this.createPricingModel()

        this.$emit('nextStep', { step: this.step })
      } catch (error) {
        console.log(error)
      } finally {
        this.isCreatePricingLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.custom_card {
  border-radius: 12px !important;
  border: 1px solid #d0d3db !important;
}
.primary-color {
  color: #2661ff;
}
.theme--light.v-divider {
  border-color: #bdbdbd !important;
}
.color-picker-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 20px;
  height: 100%;
}
.color-swatch {
  cursor: pointer;
  height: 25px;
  width: 25px;
  border: 2px solid #bdbdbd; /* Border styling */
  border-radius: 20px; /* To make it a circle */
  transition: border-radius 200ms ease-in-out;
}
</style>
